import type { BaseEntity, SimpleTableProps, FieldValue } from "./types";
import type { SimpleTableSetupResult } from "./SimpleTableSetup";
import type { SimpleTableStateSetupResult } from "./SimpleTableStateSetup";
import type { SimpleTableCallbackWrappersResult } from "./SimpleTableCallbacksSetup";
import type { SimpleTableContextValue } from "./contexts/SimpleTableContext";
import { buildStateSlice } from "./SimpleTableContextValue/state-slice";
import {
  buildCallbacksSlice,
  type ComposedTableCallbacks,
} from "./SimpleTableContextValue/callbacks-slice";

/**
 * Create the context value object that will be passed to
 * SimpleTableContextProvider. This function organizes all state, callbacks,
 * refs, and configuration into a single object.
 *
 * The two large slices live in `SimpleTableContextValue/` (module-size lane
 * L17): `state-slice.ts` (state + the FilterContext projection) and
 * `callbacks-slice.ts` (the explicit callback allow-list). This module owns the
 * top-level shape and the prop pass-through.
 */
export function createSimpleTableContextValue<TData extends BaseEntity>(
  setup: SimpleTableSetupResult<TData>,
  stateSetup: SimpleTableStateSetupResult,
  callbackWrappers: SimpleTableCallbackWrappersResult,
  callbacks: ComposedTableCallbacks,
  props: Partial<SimpleTableProps<TData>> & Pick<SimpleTableProps<TData>, "hooks">
) {
  const {
    config,
    visibleFields,
    data,
    processedData,
    paginatedData,
    totalPages,
    appUser,
    roleName,
    openModal,
    containerRef,
    tableHeaderScrollRef,
    tableBodyRef,
  } = setup;

  const contextValue = {
    config,
    visibleFields,
    data,
    processedData,
    paginatedData,
    totalPages,
    // The filter values are assembled from FilterContext (FilterValue =
    // unknown) and adapted to this state slice's shape; narrow to the
    // context's state type at this type-erased boundary.
    state: buildStateSlice<TData>(
      setup,
      stateSetup,
      callbackWrappers
    ) as unknown as SimpleTableContextValue<TData>["state"],
    // The composed callbacks arrive as a loose `(...args: never[]) => unknown`
    // map (two callback-hook variants feed this set); each is assigned to its
    // precise signature on the context value.
    callbacks: buildCallbacksSlice(
      callbacks
    ) as unknown as SimpleTableContextValue<TData>["callbacks"],
    refs: {
      containerRef,
      tableHeaderScrollRef,
      tableBodyRef,
    },
    auth: {
      appUser,
      roleName,
    },
    hooks: props.hooks,
    utils: {
      getColumnStyleCallback: stateSetup.getColumnStyleCallback,
    },
    groupingContext: props.groupingContext,
    groupableFields: props.groupableFields,
    openModal,
    onEdit: props.onEdit,
    onView: props.onView,
    onDelete: props.onDelete,
    // Context value shape uses an unkeyed payload signature so cell editors can
    // call with partial updates. Adapt the typed `(item: TData) => Promise<void>`
    // from SimpleTableProps to that shape here.
    onInlineEdit: props.onInlineEdit
      ? (payload: Record<string, FieldValue>) =>
          (props.onInlineEdit as (item: TData) => Promise<void>)(payload as unknown as TData)
      : undefined,
    onCreate: props.onCreate,
    onBulkAction: props.onBulkAction,
    onOpenDetailModal: props.onOpenDetailModal,
    savedViewsControls: props.savedViewsControls,
    configSavedViews: config.savedViews,
    loadMore: props.loadMore,
    hasNextPage: props.hasNextPage,
    isFetchingNextPage: props.isFetchingNextPage,
    autoScrollEnabled: props.autoScrollEnabled,
    propTotalItems: props.totalItems,
    propTotalPages: props.totalPages,
    propOnPageChange: props.onPageChange,
    error: props.error,
    // Needed so the body can show a row SKELETON on first load instead of the
    // "No <entity> found" empty state, which read as "you have no pipeline"
    // while the query was still in flight.
    isLoading: props.isLoading,
    hideColumnHeaders: props.hideColumnHeaders,
    hideTableBody: props.hideTableBody,
  };

  return contextValue;
}

export type SimpleTableContextValueType = ReturnType<typeof createSimpleTableContextValue>;
